import { useState, useEffect } from "react";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus,
  FileText,
  Download,
  Upload,
  Eye,
  Clock,
  User,
  Award,
  Trash2,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "../ui/badge";
import { examTypesCollection } from "@/db/collection";
import { useAuth } from "@/App";
const ResultsManagement = ({ permissions }) => {
  const [students, setStudents] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedMarksheet, setSelectedMarksheet] = useState(null);
  const [showMarksheetModal, setShowMarksheetModal] = useState(false);
  const [classes, setClasses] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [formData, setFormData] = useState({
    student_id: "",
    exam_type: "",
    exam_date: new Date().toISOString().split("T")[0],
    subjects: [],
  });
  const examTypes = examTypesCollection();
  const { user } = useAuth();
  // Exam metadata form for bulk upload
  const [examMetadata, setExamMetadata] = useState({
    examType: "",
    customExamType: "",
    className: "",
    examDate: new Date().toISOString().split("T")[0],
    subjects: [],
  });

  const [subjectForm, setSubjectForm] = useState({
    name: "",
    maxMarks: "",
  });

  const today = new Date().toISOString().split("T")[0];
  const [currentSubject, setCurrentSubject] = useState({
    subject_name: "",
    marks: "",
    max_marks: "",
  });

  const canUpdate = permissions?.can_update_results || false;

  useEffect(() => {
    fetchStudents();
    fetchResults();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await apiClient.get("/v1/warden/students/my-students");
      const fetchedStudents = response.data.students || [];
      setStudents(fetchedStudents);
      const uniqueClasses = [
        ...new Set(fetchedStudents.map((s) => s.class_name).filter(Boolean)),
      ];
      setClasses(uniqueClasses.sort());
    } catch (error) {
      console.error(error);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await apiClient.get("/v1/warden/results/recent");
      setResults(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const addSubject = () => {
    if (
      !currentSubject.subject_name ||
      !currentSubject.marks ||
      !currentSubject.max_marks
    ) {
      toast.error("Please fill all subject fields");
      return;
    }

    const marks = parseFloat(currentSubject.marks);
    const maxMarks = parseFloat(currentSubject.max_marks);

    if (isNaN(marks)) {
      toast.error("Please enter valid marks obtained");
      return;
    }

    if (isNaN(maxMarks) || maxMarks <= 0) {
      toast.error("Please enter valid max marks");
      return;
    }

    // check marks obtain greater than max marks
    if (marks > maxMarks) {
      toast.error("Marks obtained cannot be greater than max marks");
      return;
    }


    setFormData({
      ...formData,
      subjects: [...formData.subjects, { ...currentSubject }],
    });
    setCurrentSubject({ subject_name: "", marks: "", max_marks: "" });
  };

  const removeSubject = (index) => {
    setFormData({
      ...formData,
      subjects: formData.subjects.filter((_, i) => i !== index),
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (formData.subjects.length === 0) {
      toast.error("Please add at least one subject");
      return;
    }
    // Determine final exam type
    const finalExamType =
      formData?.exam_type === "custom"
        ? formData?.custom_exam_type?.trim()
        : formData?.exam_type;
    // replace final exam upper to lower case and in space use -
    finalExamType?.toLowerCase().replaceAll(/\s+/g, "-");
    setLoading(true);
    try {
      await apiClient.post("/v1/warden/results/add", {
        ...formData,
        exam_type: finalExamType,
      });
      toast.success("Results added successfully");
      setShowAddDialog(false);
      setFormData({
        student_id: "",
        exam_type: "",
        custom_exam_type: "",
        exam_date: new Date().toISOString().split("T")[0],
        subjects: [],
      });
      fetchResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add results");
    } finally {
      setLoading(false);
    }
  };

  // Add subject to exam metadata
  const handleAddSubject = () => {
    if (!subjectForm.name.trim()) {
      toast.error("Please enter subject name");
      return;
    }
    
    const maxMarks = parseFloat(subjectForm.maxMarks);
    if (isNaN(maxMarks) || maxMarks <= 0) {
      toast.error("Please enter valid max marks");
      return;
    }
    if (examMetadata.subjects.length >= 15) {
      toast.error("Maximum 15 subjects allowed");
      return;
    }
    


    setExamMetadata((prev) => ({
      ...prev,
      subjects: [
        ...prev.subjects,
        {
          id: Date.now(),
          name: subjectForm.name.trim(),
          maxMarks: maxMarks,
        },
      ],
    }));
    setSubjectForm({ name: "", maxMarks: "" });
  };

  const handleRemoveSubject = (subjectId) => {
    setExamMetadata((prev) => ({
      ...prev,
      subjects: prev.subjects.filter((s) => s.id !== subjectId),
    }));
  };

  const handleBulkDownload = async () => {
    // Validate exam metadata
    if (!examMetadata.examType.trim()) {
      toast.error("Please enter exam name/type");
      return;
    }
    if (!examMetadata.examDate) {
      toast.error("Please select exam date");
      return;
    }
    if (examMetadata.subjects.length === 0) {
      toast.error("Please add at least one subject");
      return;
    }

    try {
      // Build subjects param: "Math:100,Science:100"
      const subjectsParam = examMetadata.subjects
        .map((s) => `${s.name}:${s.maxMarks}`)
        .join(",");

      // valid if custom exam type is selected
      if (examMetadata?.examType === "custom") {
        if (examMetadata.customExamType.trim() === "") {
          toast.error("Please enter custom exam type");
          return;
        }
      }

      const finalExamType =
        examMetadata?.examType === "custom"
          ? examMetadata?.customExamType?.trim()
          : examMetadata?.examType;
      // replace final exam upper to lower case and in space use -
      finalExamType?.toLowerCase().replaceAll(/\s+/g, "-");

      const response = await apiClient.get("/v1/warden/results/template", {
        params: {
          exam_type: finalExamType,
          exam_date: examMetadata.examDate,
          subjects: subjectsParam,
          class_name: examMetadata.className || "",
        },
        responseType: "blob",
      });

      const safeExamType = examMetadata.examType.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = `results_${safeExamType}_${examMetadata.examDate}.xlsx`;
      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, fileName, mimeType);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to download template"
      );
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error("Please select a file");
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append("file", bulkFile);

    setLoading(true);
    try {
      const response = await apiClient.post(
        "/v1/warden/results/bulk-upload",
        formDataObj,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success(
        `Results uploaded: ${response.data.successful} successful, ${response.data.failed} failed`
      );
      setBulkFile(null);
      fetchResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload results");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>
          Results Management
        </h1>
        {canUpdate && (
          <Button
            onClick={() => setShowAddDialog(true)}
            className="btn-primary text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Results
          </Button>
        )}
      </div>

      <Tabs defaultValue={canUpdate ? "add" : "view"}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="view">View Results</TabsTrigger>
          {canUpdate && <TabsTrigger value="add">Add Results</TabsTrigger>}
          {canUpdate && <TabsTrigger value="bulk">Bulk Upload</TabsTrigger>}
        </TabsList>

        <TabsContent value="view">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="p-6 text-center">
                  <p className="text-gray-500">No results found</p>
                </CardContent>
              </Card>
            ) : (
              results.map((group, index) => (
                <Card
                  key={`${group.student_id}-${group.subject}-${index}`}
                  className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500"
                >
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <User className="w-4 h-4 text-blue-500" />
                          {group.student_name}
                        </CardTitle>
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                          ID: {group.student_readable_id} | Class:{" "}
                          {group.class_name}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-400 uppercase">Available Marksheets</p>
                      {group.marksheets?.map((marksheet, mIdx) => (
                        <div
                          key={`${group.student_id}-${marksheet.exam_type}-${mIdx}`}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-blue-50 hover:border-blue-200 cursor-pointer transition-all group"
                          onClick={() => {
                            setSelectedMarksheet({
                              ...marksheet,
                              student_name: group.student_name,
                              student_readable_id: group.student_readable_id,
                              class_name: group.class_name
                            });
                            setShowMarksheetModal(true);
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-white rounded shadow-sm group-hover:bg-blue-100">
                              <FileText className="w-4 h-4 text-blue-500" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-700">{marksheet.exam_type}</p>
                              <p className="text-xs text-gray-500">{new Date(marksheet.exam_date).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-blue-600">{marksheet.overall_percentage}%</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">{marksheet.grade} | {marksheet.status}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {canUpdate && (
          <TabsContent value="add">
            <Card>
              <CardHeader>
                <CardTitle>Add Individual Result</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Click "Add Results" button above to add results for a student
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canUpdate && (
          <TabsContent value="bulk">
            <Card>
              <CardHeader>
                <CardTitle>Bulk Upload Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">📋 Instructions:</h3>
                  <ol className="text-sm space-y-1 ml-4 list-decimal">
                    <li>Enter exam name/type and select exam date</li>
                    <li>Add subjects with their maximum marks</li>
                    <li>
                      Download template (pre-filled with your students and
                      subjects)
                    </li>
                    <li>Fill the marks in the template</li>
                    <li>Upload the completed file</li>
                  </ol>
                </div>

                {/* Exam Metadata Form */}
                <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                  <h4 className="font-medium text-gray-700">
                    Step 1: Exam Details
                  </h4>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="examType">Exam Name/Type *</Label>

                      {/* select input */}
                      <select
                        id="examType"
                        value={examMetadata.examType}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        required
                        onChange={(e) =>
                          setExamMetadata((prev) => ({
                            ...prev,
                            examType: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select Exam Type</option>
                        {examTypes?.map((item, index) => (
                          <option key={index} value={item.value}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="examDate">Exam Date *</Label>
                      <Input
                        id="examDate"
                        type="date"
                        max={today}
                        value={examMetadata.examDate}
                        onChange={(e) =>
                          setExamMetadata((prev) => ({
                            ...prev,
                            examDate: e.target.value,
                          }))
                        }
                        className="mt-1"
                      />
                    </div>
                    {/* classes */}
                    <div>
                      <Label htmlFor="className">Class (Optional)</Label>
                      <select
                        id="className"
                        className="w-full px-3 py-2 border rounded-lg mt-1"
                        value={examMetadata.className}
                        onChange={(e) =>
                          setExamMetadata((prev) => ({
                            ...prev,
                            className: e.target.value,
                          }))
                        }
                      >
                        <option value="">All Classes</option>
                        {classes?.map((cls) => (
                          <option key={cls} value={cls}>
                            {cls}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* custom exam type  */}
                    {examMetadata.examType === "custom" && (
                      <div className="col-span-2">
                        <Label htmlFor="customExamType">
                          Custom Exam Type *
                        </Label>
                        <Input
                          id="customExamType"
                          type="text"
                          required
                          placeholder="e.g., Custom Exam Type"
                          value={examMetadata.customExamType}
                          onChange={(e) =>
                            setExamMetadata((prev) => ({
                              ...prev,
                              customExamType: e.target.value,
                            }))
                          }
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Subjects Form */}
                <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                  <h4 className="font-medium text-gray-700">
                    Step 2: Add Subjects
                  </h4>

                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label htmlFor="subjectName">Subject Name</Label>
                      <Input
                        id="subjectName"
                        placeholder="e.g., Mathematics"
                        value={subjectForm.name}
                        onChange={(e) =>
                          setSubjectForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        className="mt-1"
                      />
                    </div>
                    <div className="w-32">
                      <Label htmlFor="maxMarks">Max Marks</Label>
                      <Input
                        id="maxMarks"
                        type="number"
                        placeholder="100"
                        value={subjectForm.maxMarks}
                        onChange={(e) =>
                          setSubjectForm((prev) => ({
                            ...prev,
                            maxMarks: e.target.value,
                          }))
                        }
                        className="mt-1"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleAddSubject}
                      className="btn-primary text-white"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Added Subjects List */}
                  {examMetadata.subjects.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        Added Subjects ({examMetadata.subjects.length}):
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {examMetadata.subjects.map((subj) => (
                          <div
                            key={subj.id}
                            className="flex items-center gap-2 bg-white border rounded-full px-3 py-1 text-sm"
                          >
                            <span>{subj.name}</span>
                            <span className="text-gray-400">|</span>
                            <span className="text-blue-600 font-medium">
                              {subj.maxMarks}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveSubject(subj.id)}
                              className="text-red-500 hover:text-red-700 ml-1"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Download Template */}
                <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                  <h4 className="font-medium text-gray-700">
                    Step 3: Download & Upload
                  </h4>

                  <Button
                    onClick={handleBulkDownload}
                    variant="outline"
                    className="w-full"
                    disabled={
                      !examMetadata.examType ||
                      !examMetadata.examDate ||
                      examMetadata.subjects.length === 0
                    }
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                    {examMetadata.examType && examMetadata.subjects.length > 0 && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({examMetadata.examType} -{" "}
                        {examMetadata.subjects.length} subjects)
                      </span>
                    )}
                  </Button>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-white">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setBulkFile(e.target.files[0])}
                      className="hidden"
                      id="results-bulk-file"
                    />
                    <label
                      htmlFor="results-bulk-file"
                      className="cursor-pointer"
                    >
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">
                        {bulkFile
                          ? bulkFile.name
                          : "Click to select completed file"}
                      </p>
                    </label>
                  </div>

                  {bulkFile && (
                    <Button
                      onClick={handleBulkUpload}
                      disabled={loading}
                      className="w-full btn-primary text-white"
                    >
                      {loading ? "Uploading..." : "Upload Results"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Add Results Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Exam Results</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Select Student *</Label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={formData.student_id}
                  onChange={(e) =>
                    setFormData({ ...formData, student_id: e.target.value })
                  }
                  required
                >
                  <option value="">Select a student</option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name} - {student.class_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Exam Type *</Label>

                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={formData.exam_type}
                  onChange={(e) =>
                    setFormData({ ...formData, exam_type: e.target.value })
                  }
                  required
                >
                  <option value="">Select an exam type</option>
                  {examTypes?.map((item, index) => (
                    <option key={index} value={item?.value}>
                      {item?.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* custom exam type  */}
            {formData.exam_type === "custom" && (
              <div>
                <Label>Custom Exam Type</Label>
                <Input
                  type="text"
                  required
                  placeholder="Enter Custom Exam Type"
                  value={formData.custom_exam_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      custom_exam_type: e.target.value,
                    })
                  }
                />
              </div>
            )}
            <div>
              <Label>Exam Date *</Label>
              <Input
                type="date"
                max={today}
                value={formData.exam_date}
                onChange={(e) =>
                  setFormData({ ...formData, exam_date: e.target.value })
                }
                required
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Add Subjects</h3>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <Input
                  placeholder="Subject Name"
                  value={currentSubject.subject_name}
                  onChange={(e) =>
                    setCurrentSubject({
                      ...currentSubject,
                      subject_name: e.target.value,
                    })
                  }
                />
                <Input
                  type="number"
                  placeholder="Marks Obtained"
                  value={currentSubject.marks}
                  onChange={(e) =>
                    setCurrentSubject({
                      ...currentSubject,
                      marks: e.target.value,
                    })
                  }
                />
                <Input
                  type="number"
                  placeholder="Max Marks"
                  value={currentSubject.max_marks}
                  onChange={(e) =>
                    setCurrentSubject({
                      ...currentSubject,
                      max_marks: e.target.value,
                    })
                  }
                />
              </div>

              <Button
                type="button"
                onClick={addSubject}
                variant="outline"
                className="w-full mb-4"
              >
                Add Subject
              </Button>

              {formData.subjects.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Added Subjects:</p>
                  {formData.subjects.map((sub, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-2 bg-gray-50 rounded"
                    >
                      <span>
                        {sub.subject_name}: {sub.marks}/{sub.max_marks}
                      </span>
                      <Button
                        type="button"
                        onClick={() => removeSubject(idx)}
                        variant="ghost"
                        size="sm"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary text-white"
            >
              {loading ? "Adding..." : "Add Results"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Marksheet Detail Modal */}
      <Dialog open={showMarksheetModal} onOpenChange={setShowMarksheetModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-bold" style={{ color: "#2C5F7F" }}>
                    Digital Marksheet
                  </DialogTitle>
                  <p className="text-sm text-gray-500">
                    {selectedMarksheet?.exam_type}
                  </p>
                </div>
              </div>
            </div>
          </DialogHeader>

          {selectedMarksheet && (
            <div className="space-y-6 pt-4">
              {/* Marksheet Layout */}
              <div className="border-2 border-blue-100 rounded-xl p-6 bg-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 opacity-50 z-0" />
                
                {/* Header Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 relative z-10">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Student Name</p>
                    <p className="text-sm font-bold text-gray-700">{selectedMarksheet.student_name}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Student ID</p>
                    <p className="text-sm font-bold text-gray-700">{selectedMarksheet.student_readable_id}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Class</p>
                    <p className="text-sm font-bold text-gray-700">{selectedMarksheet.class_name}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Exam Date</p>
                    <p className="text-sm font-bold text-gray-700">{new Date(selectedMarksheet.exam_date).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Subject Table */}
                <div className="overflow-hidden rounded-lg border border-gray-200 mb-8 relative z-10">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-blue-600 text-white uppercase text-[10px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Subject Name</th>
                        <th className="px-4 py-3 text-center">Marks Obtained</th>
                        <th className="px-4 py-3 text-center">Max Marks</th>
                        <th className="px-4 py-3 text-center">Class Average</th>
                        <th className="px-4 py-3 text-right">Percentage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedMarksheet.subjects.map((sub, idx) => {
                        const classAvg = (() => {
                          const stats = selectedMarksheet.global_stats?.subject_averages;
                          if (!stats) return 'N/A';
                          const subName = (sub.subject_name || sub.subject || '')?.toString().toLowerCase().trim();
                          if (!subName) return 'N/A';
                          const directKey = Object.keys(stats).find(k => k?.toString().toLowerCase().trim() === subName);
                          if (directKey && stats[directKey]?.average !== undefined && stats[directKey]?.average !== null) {
                            return stats[directKey].average;
                          }
                          const fuzzyKey = Object.keys(stats).find(k => {
                            const kNorm = k?.toString().toLowerCase().trim();
                            return kNorm && (kNorm.startsWith(subName) || subName.startsWith(kNorm));
                          });
                          if (fuzzyKey && stats[fuzzyKey]?.average !== undefined && stats[fuzzyKey]?.average !== null) {
                            return stats[fuzzyKey].average;
                          }
                          return 'N/A';
                        })();
                        
                        return (
                          <tr key={idx} className="hover:bg-blue-50 transition-colors">
                            <td className="px-4 py-3 font-semibold text-gray-700">{sub.subject_name}</td>
                            <td className="px-4 py-3 text-center font-bold text-blue-600">{sub.marks_obtained}</td>
                            <td className="px-4 py-3 text-center text-gray-500">{sub.total_marks}</td>
                            <td className="px-4 py-3 text-center font-semibold text-gray-600">{classAvg}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-bold ${sub.percentage >= 40 ? 'text-green-600' : 'text-red-600'}`}>
                                {sub.percentage}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 font-bold border-t-2 border-blue-100">
                      <tr>
                        <td className="px-4 py-4 text-blue-600">GRAND TOTAL</td>
                        <td className="px-4 py-4 text-center text-blue-600 text-lg">
                          {selectedMarksheet.total_obtained}
                        </td>
                        <td className="px-4 py-4 text-center text-gray-500">
                          {selectedMarksheet.total_max}
                        </td>
                        <td className="px-4 py-4 text-center text-gray-400 font-normal">
                          -
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className={`text-lg ${selectedMarksheet.overall_percentage >= 40 ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedMarksheet.overall_percentage}%
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Performance Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
                  <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Overall Grade</p>
                    <p className="text-3xl font-black text-blue-600">{selectedMarksheet.grade}</p>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Pass Status</p>
                    <p className={`text-2xl font-black ${selectedMarksheet.status === 'PASSED' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedMarksheet.status}
                    </p>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                    <p className="text-[10px] font-bold text-yellow-500 uppercase mb-1">Class Highest</p>
                    <p className="text-3xl font-black text-yellow-600">
                      {selectedMarksheet.global_stats?.class_highest_percentage !== undefined && selectedMarksheet.global_stats?.class_highest_percentage !== null
                        ? `${selectedMarksheet.global_stats.class_highest_percentage}%`
                        : 'N/A'}
                    </p>
                  </div>
                  {selectedMarksheet.rank ? (
                    <div className="flex flex-col items-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                      <p className="text-[10px] font-bold text-purple-400 uppercase mb-1">Class Rank</p>
                      <p className="text-3xl font-black text-purple-600">#{selectedMarksheet.rank}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Remarks</p>
                      <p className="text-sm font-medium text-gray-600 text-center italic">
                        {selectedMarksheet.overall_percentage >= 75 ? "Excellent Performance!" : 
                         selectedMarksheet.overall_percentage >= 60 ? "Good Job, keep improving." : 
                         selectedMarksheet.overall_percentage >= 40 ? "Can do better." : "Needs hard work."}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Grading Scheme Reference */}
              <div className="bg-gray-50/50 border border-gray-200/65 rounded-xl p-4 shadow-none">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5 uppercase tracking-wider mb-2.5">
                  <Award className="w-3.5 h-3.5 text-blue-500" />
                  Grading & Evaluation Scheme Reference
                </p>
                <div className="grid grid-cols-4 md:grid-cols-7 gap-1 text-center font-semibold text-[11px] text-gray-500">
                  <div className="p-1 bg-green-50 text-green-700 rounded border border-green-100">
                    <span className="block font-bold">A+</span> &ge;90%
                  </div>
                  <div className="p-1 bg-blue-50 text-blue-700 rounded border border-blue-100">
                    <span className="block font-bold">A</span> 80-89%
                  </div>
                  <div className="p-1 bg-indigo-50 text-indigo-700 rounded border border-indigo-100">
                    <span className="block font-bold">B</span> 70-79%
                  </div>
                  <div className="p-1 bg-yellow-50 text-yellow-700 rounded border border-yellow-100">
                    <span className="block font-bold">C</span> 60-69%
                  </div>
                  <div className="p-1 bg-orange-50 text-orange-700 rounded border border-orange-100">
                    <span className="block font-bold">D</span> 50-59%
                  </div>
                  <div className="p-1 bg-amber-50/70 text-amber-700 rounded border border-amber-100">
                    <span className="block font-bold">E</span> 40-49%
                  </div>
                  <div className="p-1 bg-red-50 text-red-700 rounded border border-red-100">
                    <span className="block font-bold">F</span> &lt;40%
                  </div>
                </div>
                <p className="text-[10px] text-center italic text-gray-400 mt-2">
                  * Minimum passing criteria is <strong>33%</strong>. Overall Grade is calculated on the cumulative percentage of all subjects.
                </p>
              </div>

              <div className="flex justify-end gap-3 no-print">
                <Button variant="outline" onClick={() => window.print()} className="gap-2">
                  <Download className="w-4 h-4" />
                  Print Marksheet
                </Button>
                <Button 
                  onClick={() => setShowMarksheetModal(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResultsManagement;
